import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Ops } from '@willie/schemas'
import { useTRPC, type TRPCOutputs } from '~/utils/trpc'

type MeterMakerRow = TRPCOutputs['ops']['meterMakers']['list'][number]

export interface MeterMakerOption {
  slug: string
  label: string
}

export interface MeterMakerCatalog {
  options: MeterMakerOption[]
  labelOf: (slug: string | null | undefined) => string
  isLoading: boolean
  isError: boolean
}

const toOption = (row: MeterMakerRow): MeterMakerOption => ({ slug: row.slug, label: row.label })

/**
 * Catalog makers, plus any already-selected slug the catalog does not list — which a
 * hand-edited URL can still select. A Select whose value matches no item renders an
 * empty trigger the user cannot clear.
 */
export const withSelectedMakers = (
  { options }: Pick<MeterMakerCatalog, 'options'>,
  selected: readonly string[]
): MeterMakerOption[] => {
  const missing = selected
    .filter((slug) => !options.some((maker) => maker.slug === slug))
    .map((slug) => ({ slug, label: Ops.MeterMakers.humanizeMeterMakerSlug(slug) }))
  return missing.length > 0 ? [...missing, ...options] : options
}

export const useMeterMakerCatalog = (): MeterMakerCatalog => {
  const trpc = useTRPC()
  const makersQuery = useQuery(trpc.ops.meterMakers.list.queryOptions())

  const options = useMemo(() => (makersQuery.data ?? []).map(toOption), [makersQuery.data])
  const labelBySlug = useMemo(() => new Map(options.map((maker) => [maker.slug, maker.label])), [options])

  const labelOf = (slug: string | null | undefined): string => {
    if (!slug) return '—'
    return labelBySlug.get(slug) ?? Ops.MeterMakers.humanizeMeterMakerSlug(slug)
  }

  return { options, labelOf, isLoading: makersQuery.isLoading, isError: makersQuery.isError }
}
